import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'headerBlock',
  'featuredImage',
  'breadcrumbs',
  'infoRowContainer',
  'infoRow',
  'nameColumn',
  'name',
  'descriptionColumn',
  'description',
  'myShowroomIndicator',
  'myShowroomIndicatorIcon',
  'myShowroomIndicatorText',
  'anchorLinksContainer',
  'mapSection',
  'mapContainer',
  'hoursColumn',
  'hoursHeading',
  'hoursTable',
  'supportBlock',
  'onDisplaySection',
  'onDisplayHeading',
  'onDisplayTitle',
  'onDisplayContent',
  'onDisplayGrid',
  'onDisplayItem',
  'sectionSeparator'
]);

/**
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 */
export const showroomDetailsStyles = helpers.defineMultiStyleConfig({
  baseStyle: () => ({
    root: {},

    headerBlock: {
      mb: 6,
    },

    featuredImage: {
      minHeight: {base: '200px', md: '300px', lg: '400px'},
      mb: 6,
      overflow: 'hidden',
      position: 'relative',
      w: '100%',
      sx: {
        img: {
          maxWidth: '100%',
          height: 'auto',
          width: '100%',
        },
      },
    },

    breadcrumbs: {
      marginTop: {base: '48px', lg: '46px'},
    },

    infoRowContainer: {
      marginTop: {base: '32px', lg: '56px'},
      marginBottom: {base: '38px', lg: '57px'},
      display: 'flex',
      flexDirection: 'column',
    },

    infoRow: {
      alignItems: {base: 'flex-start', lg: 'flex-end'},
      display: 'flex',
      flexDirection: {base: 'column', lg: 'row'},
      gap: {base: '12px', lg: '150px'},
    },

    nameColumn: {
      flex: {base: '1', lg: '1'},
    },

    nameStore: {
      color: 'brandColors.greyscale90',
      fontSize: {base: '40px', lg: '72px'},
      fontWeight: '400',
      lineHeight: '120%',
      letterSpacing: {base: '-0.4px;', lg: '-0.72px;'},
    },

    descriptionColumn: {
      display: 'flex',
      alignItems: 'flex-start',
      flex: {base: '1', lg: '1'},
      flexDirection: 'column',
      fontSize: {base: '16px', lg: '20px'},
      lineHeight: {base: '160%', lg: '150%'},
    },

    description: {
      color: 'gray.600',
      fontSize: {base: 'md', lg: 'lg'},
      lineHeight: 'tall',
    },

    myShowroomIndicator: {
      alignItems: 'center',
      backgroundColor: 'rgba(198, 189, 161, 0.15)',
      borderRadius: '4px',
      color: 'brandColors.greyscale90',
      display: 'flex',
      flexDirection: 'row',
      padding: '10px 12px',
      gap: '8px',
      marginTop: {base: '32px', lg: '20px'},
    },

    myShowroomIndicatorIcon: {
      boxSize: {base: '12px', lg: '14px'},
    },

    myShowroomIndicatorText: {
      color: 'brandColors.greyscale90',
      fontSize: '14px',
      fontWeight: '500',
    },

    anchorLinksContainer: {
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      paddingBottom: {base: '48px', lg: '35px'},
      paddingTop: {base: '32px', lg: '23px'},
      sx: {
        '.anchor-links-container': {
          marginBottom: 0,
        },
        'a:not(:last-of-type)': {
          color: 'brandColors.greyscale90',
          fontSize: {base: '14px', lg: '18px'},
          fontWeight: '500',
          lineHeight: '120%',
        },
        '.chakra-link': {
          gap: '6px',
        },
        '.chakra-link .chakra-text': {
          color: 'brandColors.greyscale90',
          fontSize: {base: '14px', lg: '18px'},
          fontWeight: '500',
          lineHeight: '120%',
        },
        'img': {
          maxWidth: {base: '12px', lg: '16px'},
          maxHeight: {base: '12px', lg: '16px'},
        },
      },
    },

    mapSection: {
      gap: 0,
      templateColumns: {base: '1fr', lg: 'auto 440px'},
      marginX: {base: '-20px', lg: 0},
    },

    mapContainer: {
      h: {base: '640px', lg: '100%'},
      overflow: 'hidden',
      position: 'relative',
    },

    hoursColumn: {
      backgroundColor: 'brandColors.secondaryBG',
      padding: {base: '40px 20px', lg: '56px 50px 50px'},
    },

    hoursHeading: {
      fontSize: {base: '18px', lg: '22px'},
      fontWeight: '500',
      lineHeight: '120%',
      letterSpacing: {base: 'normal', lg: '-0.22px;'},
      mb: '16px',
    },

    hoursTable: {
      'table': {
        borderCollapse: 'collapse',
        fontSize: {base: '14px', lg: '16px'},
        width: '100%',
      },
      'th, td': {
        padding: '0 8px 0 0',
        textAlign: 'left',
        verticalAlign: 'top',
        lineHeight: '1.4',
        whiteSpace: 'nowrap',
      },
      'th': {
        fontWeight: 'medium',
        color: 'gray.700',
        paddingRight: '16px',
      },
      'td': {
        padding: '0 8px 8px 0',
        verticalAlign: 'top',
        lineHeight: '1.4',
        whiteSpace: 'nowrap',
      },
      'td:first-of-type': {
        fontWeight: 'medium',
        color: 'gray.700',
        paddingRight: '16px',
      },
      'td:nth-of-type(2)': {
        color: 'gray.600',
        paddingRight: '16px',
      },
      'td:last-child': {
        color: 'gray.600',
        textAlign: 'right',
        paddingRight: '0',
      },
    },

    supportBlock: {
      sx: {
        '.support-block-container': {
          paddingTop: 0,
          paddingBottom: 0,
        },
        '.component--container .chakra-link': {
          borderColor: 'black',
          bg: 'transparent',
          textDecoration: 'none',
          _hover: {
            bg: 'white',
            borderColor: 'black',
            color: 'white',
            textDecoration: 'none',
          },
        },
      },
    },

    onDisplaySection: {
      display: {base: 'flex', lg: 'grid'},
      gridTemplateColumns: {lg: '290px 1fr 1fr'},
      direction: {base: 'column', lg: 'row'},
      gridColumnGap: {lg: '40px'},
      rowGap: {base: '24px'},
      marginTop: {base: '40px', lg: '60px'},
      marginBottom: {base: '40px', lg: '60px'},
    },

    onDisplayHeading: {
      flex: {base: '1', lg: '1'},
      minW: {lg: '33.33%'},
    },

    onDisplayTitle: {
      fontSize: {base: '28px', lg: '40px'},
      fontWeight: '400',
      lineHeight: '120%',
      letterSpacing: {base: '-0.28px', lg: '-0.4px;'},
    },

    onDisplayContent: {
      flex: {base: '1', lg: '2'},
      gridColumn: {lg: '2 / 4'},
    },

    onDisplayGrid: {
      display: 'grid',
      gridTemplateColumns: {base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)'},
      gridColumnGap: {base: '12px', md: '12px', lg: '6px'},
      gridRowGap: {base: '16px', md: '16px', lg: '28px'},
    },

    onDisplayItem: {
      // Each icon item - adjust width based on how many you want per row
      flexGrow: 0,
      flexShrink: 0,
      sx: {
        '.component--icon': {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: '16px',
        },
        '.chakra-text': {
          fontSize: '16px',
          fontWeight: '400',
          lineHeight: '120%',
          marginTop: 0,
        },
        'img': {
          maxWidth: '32px',
          maxHeight: '32px',
        },
      },
    },

    sectionSeparator: {
      borderTop: '1px solid',
      borderColor: 'brandColors.greyscale10',
      margin: {base: '40px auto', lg: '60px auto'},
      maxWidth: {base: '100%', lg: '1280px'},
      width: {base: 'calc(100% - 40px)', lg: '100%'},
    },

    sectionWrapper: {
      maxWidth: {base: '100%', lg: '1440px'},
      marginX: 'auto',
      paddingX: {base: '20px', lg: '80px'},
      width: '100%',
    },
  }),
});
